import type {
    ISetTouringHoursRequest,
    ITouringPeriod,
    TouringDay,
} from 'src/services/api';

export type WindowForm = { open_time: string; close_time: string };
// A weekday is "open" iff it has at least one window; there is no separate
// enable/disable flag.
export type DayForm = { windows: WindowForm[] };
export type SpecialDayForm = {
    date: string;
    is_closed: boolean;
    windows: WindowForm[];
};

export const days: Array<{ label: string; shortLabel: string; value: TouringDay }> = [
    { label: 'Monday', shortLabel: 'Mon', value: 0 },
    { label: 'Tuesday', shortLabel: 'Tue', value: 1 },
    { label: 'Wednesday', shortLabel: 'Wed', value: 2 },
    { label: 'Thursday', shortLabel: 'Thu', value: 3 },
    { label: 'Friday', shortLabel: 'Fri', value: 4 },
    { label: 'Saturday', shortLabel: 'Sat', value: 5 },
    { label: 'Sunday', shortLabel: 'Sun', value: 6 },
];

export const defaultWindow = (): WindowForm => ({ open_time: '', close_time: '' });

// Tour windows are restricted to quarter-hour boundaries, so times are chosen
// from a dropdown of :00 / :15 / :30 / :45 options rather than a free-typed
// time input.
export const TIME_OPTIONS: string[] = Array.from({ length: 96 }, (_, i) => {
    const hours = Math.floor(i / 4);
    const minutes = String((i % 4) * 15).padStart(2, '0');
    return `${String(hours).padStart(2, '0')}:${minutes}`;
});

export const formatTimeLabel = (value: string): string => {
    const [hours, minutes] = value.split(':').map(Number);
    const meridiem = hours < 12 ? 'AM' : 'PM';
    const displayHour = hours % 12 === 0 ? 12 : hours % 12;
    return `${displayHour}:${String(minutes).padStart(2, '0')} ${meridiem}`;
};

export const emptyWeekly = (): Record<TouringDay, DayForm> => ({
    0: { windows: [] },
    1: { windows: [] },
    2: { windows: [] },
    3: { windows: [] },
    4: { windows: [] },
    5: { windows: [] },
    6: { windows: [] },
});

// The default when enabling a schedule from scratch: every day open all day.
// 23:45 is the last quarter-hour slot, keeping the end within the picker grid.
const allDayWindow = (): WindowForm => ({ open_time: '00:00', close_time: '23:45' });

export const allDayWeekly = (): Record<TouringDay, DayForm> => ({
    0: { windows: [allDayWindow()] },
    1: { windows: [allDayWindow()] },
    2: { windows: [allDayWindow()] },
    3: { windows: [allDayWindow()] },
    4: { windows: [allDayWindow()] },
    5: { windows: [allDayWindow()] },
    6: { windows: [allDayWindow()] },
});

export const toMinutes = (time: string): number => {
    if (!time) {
        return Number.NaN;
    }
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
};

const hasOverlaps = (windows: WindowForm[]): boolean => {
    const ordered = [...windows].sort((a, b) => toMinutes(a.open_time) - toMinutes(b.open_time));
    return ordered.some((window, index) => {
        const next = ordered[index + 1];
        return next ? toMinutes(window.close_time) > toMinutes(next.open_time) : false;
    });
};

// Returns the first problem with a set of windows, or null when they are valid.
// Only fully-filled windows are evaluated so partially-entered rows do not flash
// an error mid-edit; the overlap check, however, surfaces as soon as it applies.
export const getWindowsError = (windows: WindowForm[]): string | null => {
    const filled = windows.filter((window) => window.open_time && window.close_time);
    if (filled.some((window) => toMinutes(window.open_time) >= toMinutes(window.close_time))) {
        return 'End time must be after start time.';
    }
    if (hasOverlaps(filled)) {
        return 'Time windows cannot overlap.';
    }
    return null;
};

export function buildWeeklyFromPeriods(periods: ITouringPeriod[]): Record<TouringDay, DayForm> {
    const weekly = emptyWeekly();
    periods.forEach((period) => {
        weekly[period.open_day].windows.push({
            open_time: period.open_time,
            close_time: period.close_time,
        });
    });
    return weekly;
}

export function buildPayload(
    timezone: string,
    maxConcurrentTours: string,
    weekly: Record<TouringDay, DayForm>,
    specialDays: SpecialDayForm[],
): ISetTouringHoursRequest {
    return {
        timezone,
        max_concurrent_tours: maxConcurrentTours ? Number(maxConcurrentTours) : null,
        periods: days.flatMap(({ value }) =>
            weekly[value].windows.map((window) => ({
                open_day: value,
                open_time: window.open_time,
                close_day: value,
                close_time: window.close_time,
            }))
        ),
        special_days: specialDays.map((specialDay) => ({
            date: specialDay.date,
            is_closed: specialDay.is_closed,
            windows: specialDay.is_closed ? [] : specialDay.windows,
        })),
    };
}

export function getDirtyKey(
    timezone: string,
    maxConcurrentTours: string,
    weekly: Record<TouringDay, DayForm>,
    specialDays: SpecialDayForm[],
): string {
    return JSON.stringify(buildPayload(timezone, maxConcurrentTours, weekly, specialDays));
}

/**
 * Returns the first validation problem with the whole form, or null when it is
 * ready to save. The caller is expected to skip validation entirely while the
 * schedule is disabled.
 */
export function getValidationError(
    timezone: string,
    maxConcurrentTours: string,
    weekly: Record<TouringDay, DayForm>,
    specialDays: SpecialDayForm[],
): string | null {
    if (!timezone) {
        return 'Choose a timezone.';
    }
    for (const { label, value } of days) {
        const day = weekly[value];
        if (day.windows.some((window) => !window.open_time || !window.close_time)) {
            return `${label} has an incomplete time window.`;
        }
        if (day.windows.some((window) => toMinutes(window.open_time) >= toMinutes(window.close_time))) {
            return `${label} has a window where the end time is not after the start time.`;
        }
        if (hasOverlaps(day.windows)) {
            return `${label} has overlapping time windows.`;
        }
    }
    const maxConcurrent = Number(maxConcurrentTours);
    if (maxConcurrentTours !== '' && (!Number.isInteger(maxConcurrent) || maxConcurrent < 1)) {
        return 'Max concurrent tours must be a whole number of 1 or more.';
    }
    for (const specialDay of specialDays) {
        if (!specialDay.date) {
            return 'Each date-specific schedule needs a date.';
        }
        if (!specialDay.is_closed && specialDay.windows.length === 0) {
            return 'Open date-specific schedules need at least one time window.';
        }
        if (specialDay.windows.some((window) => !window.open_time || !window.close_time)) {
            return `${specialDay.date} has an incomplete time window.`;
        }
        if (specialDay.windows.some((window) => toMinutes(window.open_time) >= toMinutes(window.close_time))) {
            return `${specialDay.date} has a window where the end time is not after the start time.`;
        }
        if (hasOverlaps(specialDay.windows)) {
            return `${specialDay.date} has overlapping time windows.`;
        }
    }
    return null;
}
